'use client';

import clsx from 'clsx';
import React, { useEffect } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { KanpaiGame } from '@/components/kanpaigame/kanpaigame';
import { Loader } from '@/components/loader/loader';
import { Heading } from '@/components/typography/heading';
import { useAgentsQuery } from '@/hooks/queries/useAgents';
import { useDashboardStatsQuery } from '@/hooks/queries/useDashboardStats';
import { useModelsQuery } from '@/hooks/queries/useModelsQuery';
import { useKonamiCode } from '@/hooks/useKonamiCode';
import { AgentTypeEnum, BaseAgent } from '@/types/agent';
import { envVarsProvider } from '@/utils/envVars/envVars';

import styles from './page.module.scss';

export const dynamic = 'force-dynamic';

export default function HomePage() {
  const { data: agents, error: agentsFetchError } = useAgentsQuery( { enabled: true } );
  useEffect( () => {
    if ( agentsFetchError ) {
      toast.error( 'Unable to fetch agents' );
    }
  }, [ agentsFetchError ] );

  const agentsAIDP = agents?.filter( ( agent: BaseAgent ) => agent.type == AgentTypeEnum.AIDP );
  const agentsLowCode = agents?.filter( ( agent: BaseAgent ) => agent.type == AgentTypeEnum.LOW_CODE );

  const { data: models, error: modelsFetchError } = useModelsQuery( { enabled: true } );
  useEffect( () => {
    if ( modelsFetchError ) {
      toast.error( 'Unable to fetch models' );
    }
  }, [ modelsFetchError ] );

  const { data: dashboardStats, error: statsFetchError } = useDashboardStatsQuery();
  useEffect( () => {
    if ( statsFetchError ) {
      toast.error( 'Unable to fetch invocation stats' );
    }
  }, [ statsFetchError ] );

  const konamiActivated = useKonamiCode();

  useEffect( () => {
    const appVersion = envVarsProvider.get( 'APP_VERSION' );
    console.log( `UI v${ appVersion }` );
  }, [] );

  return (
    <div className='page'>
      <ToastContainer/>
      <div className={ clsx( 'layout__row', 'pad' ) }>
        <Heading>Welcome to AIDP Studio</Heading>
      </div>

      <div className={ clsx( 'layout__row', 'pad', styles.dashboard ) }>
        <div className={ clsx( 'layout__col', styles.block ) }>
          <span className={ styles.title }>Registered AIDP Agents</span>
          { agentsFetchError ?
              <span>Error fetching agents</span> :
              <span className={ styles.count }>{agentsAIDP?.length ?? <Loader/>}</span>}
        </div>

        <div className={ clsx( 'layout__col', styles.block ) }>
          <span className={ styles.title }>Registered Low-Code Agents</span>
          {agentsFetchError ?
              <span>Error fetching agents</span> :
              <span className={ styles.count }>{agentsLowCode?.length ?? <Loader/>}</span>}
        </div>
      </div>

      <div className={ clsx( 'layout__row', 'pad', styles.dashboard ) }>
        <div className={ clsx( 'layout__col', styles.block ) }>
          <span className={ styles.title }>Registered Models</span>
          {modelsFetchError ?
              <span>Error fetching models</span> :
              <span className={ styles.count }>{models?.length ?? <Loader/>}</span>}
        </div>

        <div className={ clsx( 'layout__col', styles.block ) }>
          { konamiActivated ? (
            <KanpaiGame />
          ) : (
            <>
              <span className={ styles.title }>Total Invocations in the last 30 Days</span>
              { statsFetchError ?
                  <span>Error fetching stats</span> : (
                    <span className={ styles.count }>
                      {dashboardStats?.total_invocations.toLocaleString() ?? <Loader/>}
                    </span>
                  )}
            </>
          ) }
        </div>
      </div>
    </div>
  );
}
